import { animate } from 'motion';

const RAIL_SELECTOR = 'div.scroll-container';
const MEDIA_SELECTOR = 'img, video';
const EAGER_BUFFER = 6;

function centerOffset(rail, item) {
  return item.offsetLeft - (rail.clientWidth - item.offsetWidth) / 2;
}

// Most galleries are left-aligned (the first item starts at the left edge);
// only the About showcase keeps its items centred.
function alignOffset(rail, item) {
  if (rail.id === 'showcaseimages') return centerOffset(rail, item);
  const inset = parseFloat(getComputedStyle(rail).paddingInlineStart) || 0;
  return item.offsetLeft - inset;
}

function makeClone(item, position) {
  const clone = item.cloneNode(true);
  clone.removeAttribute('id');
  clone.removeAttribute('data-media-rail-original');
  clone.setAttribute('data-media-rail-clone', position);
  clone.setAttribute('aria-hidden', 'true');
  clone.setAttribute('tabindex', '-1');
  clone.setAttribute('draggable', 'false');
  clone.classList.remove('scroll-reveal-item', 'scroll-reveal-visible', 'media-rail-active');
  if (clone.matches('video')) {
    clone.removeAttribute('controls');
    clone.muted = true;
    clone.preload = 'metadata';
  }
  return clone;
}

function enhanceRail(rail) {
  if (rail.dataset.mediaRailEnhanced === 'true') return null;
  // Research and project galleries are plain strips: they show the few
  // available images with snap scrolling, without infinite-loop clones.
  if (rail.classList.contains('project') || rail.dataset.mediaRail === 'finite') return null;

  const originals = Array.from(rail.children)
    .filter((item) => item.matches(MEDIA_SELECTOR)
      && !item.hasAttribute('data-media-rail-clone'));
  if (originals.length < 2) return null;

  rail.dataset.mediaRailEnhanced = 'true';
  rail.classList.add('media-rail-loop');
  if (rail.id === 'showcaseimages') rail.classList.add('media-rail-preparing');
  originals.forEach((item, index) => {
    item.setAttribute('data-media-rail-original', 'true');
    item.setAttribute('draggable', 'false');
    if (rail.id !== 'showcaseimages') item.style.scrollSnapAlign = 'start';
    if (item.matches('img')) {
      item.decoding = 'async';
      // A buffer at each end loads eagerly so the loop regions measure
      // correctly right away; the middle stays lazy.
      item.loading = index < EAGER_BUFFER || index >= originals.length - EAGER_BUFFER
        ? 'eager'
        : 'lazy';
    } else {
      item.preload = index === 0 ? 'metadata' : 'none';
    }
  });

  const initialInlineSnap = rail.style.scrollSnapType;
  const initialInlineBehavior = rail.style.scrollBehavior;
  const supportsScrollEnd = 'onscrollend' in rail;
  let centers = [];
  let activeItem = null;
  let currentOriginal = originals[0];
  let jumping = false;
  let pendingJump = null;
  let interacted = false;
  let jumpAnim = null;
  let scrollTimer = null;
  let resizeFrame = null;
  let visualFrame = null;
  let measureFrame = null;
  let initFrame = null;
  let items = originals;
  let clonesBuilt = false;
  let headClones = [];
  let tailClones = [];
  let headSources = [];
  let tailSources = [];
  let period = 0;
  let tailRegionStart = 0;
  let initialized = false;

  const stopJump = () => {
    if (jumpAnim) {
      jumpAnim.stop();
      jumpAnim = null;
    }
    if (jumping) {
      jumping = false;
      restoreInlineStyle('scroll-snap-type', initialInlineSnap);
      restoreInlineStyle('scroll-behavior', initialInlineBehavior);
    }
  };

  const restoreInlineStyle = (property, value) => {
    if (value) rail.style.setProperty(property, value);
    else rail.style.removeProperty(property);
  };

  const measure = () => {
    centers = items.map((item) => item.offsetLeft + item.offsetWidth / 2);
  };

  const nearestItem = () => {
    if (!centers.length) measure();
    const target = rail.scrollLeft + rail.clientWidth / 2;
    let low = 0;
    let high = centers.length - 1;
    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if (centers[mid] < target) low = mid + 1;
      else high = mid;
    }
    if (low > 0 && Math.abs(centers[low - 1] - target) <= Math.abs(centers[low] - target)) {
      return items[low - 1];
    }
    return items[low];
  };

  const originalFor = (item) => {
    const headIndex = headClones.indexOf(item);
    if (headIndex !== -1) return headSources[headIndex];
    const tailIndex = tailClones.indexOf(item);
    if (tailIndex !== -1) return tailSources[tailIndex];
    return item;
  };

  const updateActiveVisual = () => {
    visualFrame = null;
    if (!rail.isConnected) return;
    const nextActive = nearestItem();
    if (nextActive !== activeItem) {
      if (activeItem) activeItem.classList.remove('media-rail-active');
      nextActive.classList.add('media-rail-active');
      activeItem = nextActive;
    }
    currentOriginal = originalFor(nextActive);
  };

  const scheduleActiveVisual = () => {
    if (visualFrame) return;
    visualFrame = requestAnimationFrame(updateActiveVisual);
  };

  const finishJump = () => {
    jumpAnim = null;
    restoreInlineStyle('scroll-snap-type', initialInlineSnap);
    restoreInlineStyle('scroll-behavior', initialInlineBehavior);
    jumping = false;
    measure();
    updateActiveVisual();
    rail.classList.add('media-rail-ready');
    rail.classList.remove('media-rail-preparing');
    initialized = true;
    if (pendingJump) {
      const nextJump = pendingJump;
      pendingJump = null;
      jumpTo(nextJump);
    }
  };

  // Glide to the item with a short critically-damped spring. Used for the
  // initial centering and re-centering on resize, where an instant scrollLeft
  // reads as a hard cut. Small distances and reduced-motion users skip the
  // glide and teleport as before.
  const jumpTo = (item, { immediate = false } = {}) => {
    if (!item || !rail.isConnected) return;
    if (jumping) {
      pendingJump = item;
      return;
    }
    stopJump();
    jumping = true;
    rail.style.scrollSnapType = 'none';
    rail.style.scrollBehavior = 'auto';
    const target = alignOffset(rail, item);
    const distance = Math.abs(rail.scrollLeft - target);
    const reduceMotion = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (immediate || reduceMotion || distance < 8) {
      rail.scrollLeft = target;
      updateActiveVisual();
      finishJump();
      return;
    }
    jumpAnim = animate(rail, { scrollLeft: target }, {
      type: 'spring',
      bounce: 0,
      duration: distance > rail.clientWidth * 1.2 ? 0.42 : 0.34,
      onComplete: finishJump,
    });
  };

  const gapBetween = () => (originals.length > 1
    ? originals[1].offsetLeft - (originals[0].offsetLeft + originals[0].offsetWidth)
    : 0);
  const cyclePeriod = () => {
    const last = originals[originals.length - 1];
    return last.offsetLeft + last.offsetWidth + gapBetween() - originals[0].offsetLeft;
  };
  const headExtent = () => (headClones.length
    ? originals[0].offsetLeft - headClones[0].offsetLeft
    : 0);
  const tailExtent = () => {
    if (!tailClones.length) return 0;
    const last = originals[originals.length - 1];
    const tailEnd = tailClones[tailClones.length - 1].offsetLeft
      + tailClones[tailClones.length - 1].offsetWidth;
    return tailEnd - (last.offsetLeft + last.offsetWidth);
  };

  // Build (and extend as lazy media loads) the loop regions. The head region
  // is a copy of the END of the cycle laid out left of the first original,
  // the tail region a copy of the START right of the last. Each stays at
  // least two viewports wide, so a one-cycle wrap always lands on identical
  // pixels and never runs out of content or clamps at the physical edges.
  const ensureClones = () => {
    if (!rail.isConnected) return;
    if (originals[0].offsetWidth === 0) return;
    clonesBuilt = true;
    const gap = gapBetween();
    period = cyclePeriod();
    const neededWidth = rail.clientWidth * 2 + 1;

    let guard = 0;
    while (headExtent() < neededWidth && guard < originals.length * 8) {
      const index = headSources.length
        ? (originals.indexOf(headSources[0]) - 1 + originals.length) % originals.length
        : originals.length - 1;
      const source = originals[index];
      const clone = makeClone(source, 'head');
      rail.insertBefore(clone, headClones[0] || originals[0]);
      headClones.unshift(clone);
      headSources.unshift(source);
      guard += 1;
    }

    guard = 0;
    while (tailExtent() < neededWidth && guard < originals.length * 8) {
      const index = tailSources.length
        ? (originals.indexOf(tailSources[tailSources.length - 1]) + 1) % originals.length
        : 0;
      const source = originals[index];
      const clone = makeClone(source, 'tail');
      rail.appendChild(clone);
      tailClones.push(clone);
      tailSources.push(source);
      guard += 1;
    }

    const lastOriginal = originals[originals.length - 1];
    tailRegionStart = tailClones.length
      ? tailClones[0].offsetLeft
      : lastOriginal.offsetLeft + lastOriginal.offsetWidth + gap;
    items = [...headClones, ...originals, ...tailClones];
    measure();
  };

  // Teleport by one full cycle. The visible frame is identical because the
  // clone regions repeat the cycle, so the jump is invisible; an instant set
  // avoids fighting the user's gesture.
  const wrapRail = (delta) => {
    if (!rail.isConnected || !clonesBuilt || jumping) return;
    const previousSnap = rail.style.scrollSnapType;
    const previousBehavior = rail.style.scrollBehavior;
    rail.style.scrollSnapType = 'none';
    rail.style.scrollBehavior = 'auto';
    rail.scrollLeft += delta;
    restoreInlineStyle('scroll-snap-type', previousSnap);
    restoreInlineStyle('scroll-behavior', previousBehavior);
    updateActiveVisual();
  };

  const wrapIfNeeded = () => {
    if (!clonesBuilt || period <= 0) return false;
    // Positions change as lazy media loads, so re-measure right before
    // deciding to wrap.
    measure();
    const rightEdge = rail.scrollLeft + rail.clientWidth;
    const leftEdge = rail.scrollLeft;
    if (rightEdge >= tailRegionStart) {
      wrapRail(-period);
      return true;
    }
    if (leftEdge <= originals[0].offsetLeft - rail.clientWidth) {
      wrapRail(period);
      return true;
    }
    return false;
  };

  const settle = () => {
    if (jumping || !rail.isConnected || rail.classList.contains('is-gallery-motion')) return;
    updateActiveVisual();
    wrapIfNeeded();
  };

  const onScroll = () => {
    scheduleActiveVisual();
    if (jumping || supportsScrollEnd || !rail.classList.contains('media-rail-ready')) return;
    window.clearTimeout(scrollTimer);
    scrollTimer = window.setTimeout(settle, 140);
  };

  const markInteracted = () => {
    interacted = true;
    stopJump();
  };

  const onResize = () => {
    if (resizeFrame) cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = null;
      ensureClones();
      measure();
      if (rail.classList.contains('media-rail-ready')) jumpTo(currentOriginal);
    });
  };

  const onMediaReady = () => {
    if (measureFrame) cancelAnimationFrame(measureFrame);
    measureFrame = requestAnimationFrame(() => {
      measureFrame = null;
      if (!initialized) {
        tryInit();
        return;
      }
      ensureClones();
      measure();
      if (!interacted) {
        if (rail.id === 'showcaseimages') jumpTo(currentOriginal, { immediate: true });
        else startAtFirstLeft();
      }
      else scheduleActiveVisual();
    });
  };

  const initialMediaReady = () => {
    const edgeItems = [
      ...originals.slice(0, EAGER_BUFFER),
      ...originals.slice(-EAGER_BUFFER),
    ];
    return edgeItems.every((item) => (
      item.matches('img')
        ? item.complete && item.naturalWidth > 0
        : item.readyState >= 1
    ));
  };

  const tryInit = () => {
    if (!rail.isConnected) return;
    if (!initialMediaReady()) {
      if (initFrame) cancelAnimationFrame(initFrame);
      initFrame = requestAnimationFrame(tryInit);
      return;
    }
    ensureClones();
    measure();
    if (originals[0].offsetWidth > 0) {
      if (rail.id === 'showcaseimages') {
        jumpTo(originals[0], { immediate: true });
      } else {
        startAtFirstLeft();
      }
    } else {
      initFrame = requestAnimationFrame(tryInit);
    }
  };

  // Every gallery except the About showcase starts with the first original
  // item flush to the left edge. Mandatory center-snap would pull the rail
  // back to a centered item, so snap is off while the position is set.
  const startAtFirstLeft = () => {
    const prevSnap = rail.style.scrollSnapType;
    const prevBehavior = rail.style.scrollBehavior;
    rail.style.scrollSnapType = 'none';
    rail.style.scrollBehavior = 'auto';
    rail.scrollLeft = alignOffset(rail, originals[0]);
    rail.style.scrollSnapType = prevSnap;
    rail.style.scrollBehavior = prevBehavior;
  };

  rail.addEventListener('scroll', onScroll, { passive: true });
  if (supportsScrollEnd) rail.addEventListener('scrollend', settle, { passive: true });
  rail.addEventListener('home-gallery-motion-end', settle);
  rail.addEventListener('home-gallery-control', markInteracted);
  rail.addEventListener('home-gallery-motion-wrap', wrapIfNeeded);
  rail.addEventListener('pointerdown', markInteracted, { passive: true });
  rail.addEventListener('touchstart', markInteracted, { passive: true });
  rail.addEventListener('wheel', markInteracted, { passive: true });
  originals.forEach((item) => {
    const eventName = item.matches('video') ? 'loadedmetadata' : 'load';
    if ((item.matches('img') && !item.complete)
      || (item.matches('video') && item.readyState < 1)) {
      item.addEventListener(eventName, onMediaReady, { once: true });
    }
  });
  // If the buffer has already finished loading (cache), center immediately.
  if (originals.every((item) => (item.matches('img') ? item.complete : item.readyState >= 1))) {
    onMediaReady();
  }

  const resizeObserver = typeof ResizeObserver !== 'undefined'
    ? new ResizeObserver(onResize)
    : null;
  if (resizeObserver) resizeObserver.observe(rail);

  requestAnimationFrame(tryInit);

  return () => {
    window.clearTimeout(scrollTimer);
    stopJump();
    if (initFrame) cancelAnimationFrame(initFrame);
    if (resizeFrame) cancelAnimationFrame(resizeFrame);
    if (visualFrame) cancelAnimationFrame(visualFrame);
    if (measureFrame) cancelAnimationFrame(measureFrame);
    if (resizeObserver) resizeObserver.disconnect();
    rail.removeEventListener('scroll', onScroll);
    if (supportsScrollEnd) rail.removeEventListener('scrollend', settle);
    rail.removeEventListener('home-gallery-motion-end', settle);
    rail.removeEventListener('home-gallery-control', markInteracted);
    rail.removeEventListener('home-gallery-motion-wrap', wrapIfNeeded);
    rail.removeEventListener('pointerdown', markInteracted);
    rail.removeEventListener('touchstart', markInteracted);
    rail.removeEventListener('wheel', markInteracted);
    originals.forEach((item) => {
      item.removeEventListener(item.matches('video') ? 'loadedmetadata' : 'load', onMediaReady);
      item.removeAttribute('data-media-rail-original');
      item.classList.remove('media-rail-active');
    });
    headClones.concat(tailClones).forEach((clone) => clone.remove());
    rail.classList.remove('media-rail-loop', 'media-rail-ready', 'media-rail-preparing');
    delete rail.dataset.mediaRailEnhanced;
  };
}

export function initCircularMediaRails(root = document) {
  const controllers = new Map();
  let scanFrame = null;

  const scan = () => {
    scanFrame = null;
    controllers.forEach((cleanup, rail) => {
      if (!rail.isConnected) {
        cleanup();
        controllers.delete(rail);
      }
    });
    root.querySelectorAll(RAIL_SELECTOR).forEach((rail) => {
      if (controllers.has(rail)) return;
      const cleanup = enhanceRail(rail);
      if (cleanup) controllers.set(rail, cleanup);
    });
  };

  const scheduleScan = () => {
    if (scanFrame) return;
    scanFrame = requestAnimationFrame(scan);
  };

  const observer = new MutationObserver(scheduleScan);
  observer.observe(root, { childList: true, subtree: true });
  scheduleScan();

  return () => {
    observer.disconnect();
    if (scanFrame) cancelAnimationFrame(scanFrame);
    controllers.forEach((cleanup) => cleanup());
    controllers.clear();
  };
}
